<template>
  <wrapper-page ref="wrapper">
    <div class="container-fluid py-3">
      <!-- Page Header -->
      <div class="row mb-3 align-items-center">
        <div class="col">
          <h3 class="mb-0 text-dark">Survey Travel Planner</h3>
        </div>
      </div>

      <!-- Filter Controls Card -->
      <div class="card shadow-sm mb-4">
        <div class="card-body">
          <!-- Row 1: Filter Inputs -->
          <div class="row g-3">
            <!-- Vessel Name Search Input -->
            <div class="col-md-6 col-lg-3">
              <label
                for="filter-vessel-name"
                class="form-label font-weight-bold mb-1"
              >
                Vessel Name
              </label>
              <input
                id="filter-vessel-name"
                v-model="localFilters.vesselName"
                type="text"
                class="form-control form-control-sm"
                placeholder="Search by vessel name..."
                @keyup.enter="applyFilters"
              />
            </div>

            <!-- Certificate Type Select -->
            <div class="col-md-6 col-lg-3">
              <label
                for="filter-certificate-type"
                class="form-label font-weight-bold mb-1"
              >
                Certificate Type
              </label>
              <select
                id="filter-certificate-type"
                v-model="localFilters.certificateType"
                class="form-control form-select form-control-sm"
              >
                <option :value="null">All Certificates</option>
                <option
                  v-for="cert in certificateOptions"
                  :key="cert.value"
                  :value="cert.value"
                >
                  {{ cert.text }}
                </option>
              </select>
            </div>

            <!-- Vessel Base Port Text Input -->
            <div class="col-md-6 col-lg-3">
              <label class="form-label font-weight-bold mb-1"
                >Vessel Port</label
              >
              <input
                v-model="localFilters.vesselPort"
                type="text"
                class="form-control form-control-sm"
                placeholder="Search vessel port..."
                @keyup.enter="applyFilters"
              />
            </div>

            <!-- Service Country MultiSelect -->
            <div class="col-md-6 col-lg-3">
              <div class="multiselect-align-fix">
                <label class="form-label font-weight-bold mb-1"
                  >Service Countries</label
                >
                <multi-select-dropdown
                  v-model="localFilters.serviceCountries"
                  :field="serviceCountryField"
                  :margin-bottom="0"
                />
              </div>
            </div>
          </div>

          <!-- Row 2: Action Buttons (Pushed to the right) -->
          <div class="row mt-3">
            <div class="col-12 d-flex justify-content-end">
              <button
                type="button"
                class="btn btn-sm btn-secondary me-2"
                @click="resetFilters"
              >
                Reset
              </button>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                :disabled="loading"
                @click="applyFilters"
              >
                <i class="ri-search-line align-middle me-1"></i>
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Results Card -->
      <div class="card shadow-sm">
        <div class="card-body">
          <!-- Table Export Buttons -->
          <table-export-buttons
            :columns="tableColumns"
            :data="searchResult"
            file-name="survey-travel-planner"
            title="Survey Travel Planner Report"
          />

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <!-- Table Content -->
          <template v-else>
            <!-- Scrollable Table Wrapper -->
            <div class="table-responsive-wrapper">
              <table class="table table-hover table-striped align-middle mb-0">
                <thead class="table-light sticky-top">
                  <tr>
                    <th
                      v-for="col in tableColumns"
                      :key="col.key"
                      class="text-nowrap"
                    >
                      {{ col.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="searchResult.length === 0">
                    <td
                      :colspan="tableColumns.length"
                      class="text-center py-4 text-muted"
                    >
                      No records found.
                    </td>
                  </tr>
                  <tr v-for="(row, idx) in searchResult" :key="idx">
                    <td class="text-nowrap">
                      {{ row["NOE Vessel ID"] || "-" }}
                    </td>
                    <td class="text-nowrap fw-bold">
                      {{ row["Vessel Name"] || "-" }}
                    </td>
                    <td class="text-nowrap">{{ row["Vessel Type"] || "-" }}</td>
                    <td class="text-nowrap">{{ row["IMO Number"] || "-" }}</td>
                    <td class="text-nowrap">{{ row["MMSI"] || "-" }}</td>
                    <td class="text-nowrap">{{ row["Call Sign"] || "-" }}</td>
                    <td class="text-nowrap">{{ row["Base Port"] || "-" }}</td>
                    <td class="text-nowrap">
                      {{ getCountryName(row["Base Country"]) || "-" }}
                    </td>
                    <td class="text-nowrap">
                      {{ row["Certificate Type"] || "-" }}
                    </td>
                    <td class="text-nowrap">
                      {{ formatDate(row["Due Open"]) }}
                    </td>
                    <td class="text-nowrap">
                      {{ formatDate(row["Due Close"]) }}
                    </td>
                    <td class="text-nowrap">
                      {{ formatDate(row["Expiry Date"]) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination Controls -->
            <div
              v-if="searchResult.length > 0 || pagination.page > 1"
              class="d-flex justify-content-between align-items-center mt-3"
            >
              <div class="small text-muted">
                Showing Page {{ pagination.page }} ({{ searchResult.length }}
                items)
              </div>
              <div class="btn-group">
                <button
                  class="btn btn-sm btn-outline-secondary"
                  :disabled="pagination.page <= 1 || loading"
                  @click="changePage(pagination.page - 1)"
                >
                  Previous
                </button>
                <button
                  class="btn btn-sm btn-outline-secondary"
                  :disabled="searchResult.length < pagination.limit || loading"
                  @click="changePage(pagination.page + 1)"
                >
                  Next
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </wrapper-page>
</template>

<script>
import WrapperPage from "@/components/layout/WrapperPage.vue";
import TableExportButtons from "@/components/tableExport/TableExportButtons.vue";
import MultiSelectDropdown from "@/components/ui/MultiSelectDropdown.vue";
import { getSurveyTravelPlanner } from "@/apis/vessels";
import { notifyError } from "@/helpers/notification";
import { getCountryName } from "@/helpers/generic";
import { CountryOptions } from "@/constants/address";

export default {
  name: "SurveyTravelPlanner",
  components: {
    WrapperPage,
    TableExportButtons,
    MultiSelectDropdown,
  },
  data() {
    return {
      loading: false,
      searchResult: [],
      countryOptionsList: [],

      certificateOptions: [
        { text: "CYC", value: "CYC" },
        { text: "SCYC", value: "SCYC" },
        { text: "NCV", value: "NCV" },
        { text: "Other", value: "Other" },
      ],

      localFilters: {
        vesselName: "",
        certificateType: null,
        vesselPort: "",
        serviceCountries: [],
      },

      appliedFilters: {
        vesselName: "",
        certificateType: null,
        vesselPort: "",
        serviceCountries: [],
      },
      pagination: {
        page: 1,
        limit: 20,
      },

      tableColumns: [
        { key: "NOE Vessel ID", label: "NOE Vessel ID" },
        { key: "Vessel Name", label: "Vessel Name" },
        { key: "Vessel Type", label: "Vessel Type" },
        { key: "IMO Number", label: "IMO Number" },
        { key: "MMSI", label: "MMSI" },
        { key: "Call Sign", label: "Call Sign" },
        { key: "Base Port", label: "Base Port" },
        {
          key: "Base Country",
          label: "Base Country",
          formatter: (val) => this.getCountryName(val),
        },
        { key: "Certificate Type", label: "Certificate Type" },
        {
          key: "Due Open",
          label: "Due Open",
          formatter: (val) => this.formatDate(val),
        },
        {
          key: "Due Close",
          label: "Due Close",
          formatter: (val) => this.formatDate(val),
        },
        {
          key: "Expiry Date",
          label: "Expiry Date",
          formatter: (val) => this.formatDate(val),
        },
      ],
    };
  },
  async mounted() {
    this.initCountryOptions();
    await this.fetchPlannerData();
  },
  computed: {
    serviceCountryField() {
      return {
        name: "vesselCountries",
        placeholder: "Select vessel countries...",
        options: this.countryOptionsList,
      };
    },
  },
  methods: {
    getCountryName,
    initCountryOptions() {
      const rawCountries = Array.isArray(CountryOptions) ? CountryOptions : [];
      this.countryOptionsList = rawCountries.map((c) => ({
        text: c.text,
        value: c.value,
      }));
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      return String(dateStr).split("T")[0];
    },

    async fetchPlannerData() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
        };

        if (this.appliedFilters.vesselName) {
          params.vesselName = this.appliedFilters.vesselName.trim();
        }

        if (this.appliedFilters.certificateType) {
          params.certificateType = this.appliedFilters.certificateType;
        }

        if (this.appliedFilters.vesselPort) {
          params.vesselPort = this.appliedFilters.vesselPort.trim();
        }

        if (
          Array.isArray(this.appliedFilters.serviceCountries) &&
          this.appliedFilters.serviceCountries.length > 0
        ) {
          params.serviceCountries = this.appliedFilters.serviceCountries
            .map((v) => v.value)
            .join(",");
        }
        const response = await getSurveyTravelPlanner(params);
        this.searchResult = response?.data?.data || response?.data || [];
      } catch (err) {
        notifyError(
          this,
          err?.response?.data || err?.message || "Error fetching data"
        );
      } finally {
        this.loading = false;
      }
    },

    applyFilters() {
      this.appliedFilters = { ...this.localFilters };
      this.pagination.page = 1;
      this.fetchPlannerData();
    },

    resetFilters() {
      this.localFilters = {
        vesselName: "",
        certificateType: null,
        vesselPort: "",
        serviceCountries: [],
      };
      this.applyFilters();
    },

    changePage(newPage) {
      if (newPage < 1) return;
      this.pagination.page = newPage;
      this.fetchPlannerData();
    },
  },
};
</script>

<style scoped>
.table-responsive-wrapper {
  max-height: 65vh;
  overflow-x: auto;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 4px;
}

/* Ensure sticky table header stays above scrolling content */
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 2;
}
</style>
